import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import notify from './utils/notify';
import './PlayerManager.css';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import { FiBarChart2, FiSettings, FiGrid, FiEdit, FiTrash2, FiPlus, FiInfo } from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import PlayerModal from './PlayerModal';
import ConfirmationModal from './components/ConfirmationModal';
import BottomNav from './BottomNav';
import { useApi } from './useApi';
import { useAuth } from './AuthContext';
import { Permission } from './permissions';
import PageHeader from './components/PageHeader';

const PlayerCard = memo(({ player, canManage, onEdit, onDelete, openSwipeId, setOpenSwipeId }) => {
  const cardRef = useRef(null);
  const touchRef = useRef({ startX: 0, startY: 0, currentX: 0, isDragging: false, isScrolling: false });

  const hasActions = canManage;
  const actionsWidth = hasActions ? 140 : 0;
  const isOpen = openSwipeId === player.id;

  useEffect(() => {
    if (openSwipeId !== player.id && cardRef.current) {
      cardRef.current.style.transition = 'transform 0.3s ease';
      cardRef.current.style.transform = 'translateX(0)';
    }
  }, [openSwipeId, player.id]);

  const onTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: isOpen ? -actionsWidth : 0,
      isDragging: false,
      isScrolling: false,
    };
    if (cardRef.current) {
      cardRef.current.style.transition = 'none';
    }
  }, [isOpen, actionsWidth]);

  const onTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchRef.current.startX;
    const deltaY = touch.clientY - touchRef.current.startY;

    if (!touchRef.current.isDragging && !touchRef.current.isScrolling) {
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        touchRef.current.isScrolling = true;
        return;
      }
      if (Math.abs(deltaX) > 10) {
        touchRef.current.isDragging = true;
      }
    }

    if (touchRef.current.isScrolling || !touchRef.current.isDragging) return;

    let translateX = isOpen ? deltaX - actionsWidth : deltaX;
    translateX = Math.min(0, Math.max(-actionsWidth, translateX));
    touchRef.current.currentX = translateX;
    if (cardRef.current) {
      cardRef.current.style.transform = `translateX(${translateX}px)`;
    }
  }, [isOpen, actionsWidth]);

  const onTouchEnd = useCallback(() => {
    if (cardRef.current) {
      cardRef.current.style.transition = 'transform 0.3s ease';
    }

    if (!touchRef.current.isDragging) return;

    const threshold = actionsWidth / 2;
    if (Math.abs(touchRef.current.currentX) > threshold) {
      if (cardRef.current) {
        cardRef.current.style.transform = `translateX(-${actionsWidth}px)`;
      }
      setOpenSwipeId(player.id);
    } else {
      if (cardRef.current) {
        cardRef.current.style.transform = 'translateX(0)';
      }
      if (isOpen) setOpenSwipeId(null);
    }
  }, [actionsWidth, isOpen, setOpenSwipeId, player.id]);

  const handleCardClick = useCallback(() => {
    if (touchRef.current.isDragging) return;
    if (isOpen) {
      setOpenSwipeId(null);
      return;
    }
    if (canManage) onEdit(player);
  }, [isOpen, setOpenSwipeId, canManage, onEdit, player]);

  return (
    <div className="swipe-container">
      {hasActions && (
        <div className="swipe-actions" style={{ width: actionsWidth }}>
          <button className="swipe-action-btn swipe-edit" onClick={() => { setOpenSwipeId(null); onEdit(player); }}>
            <FiEdit size={20} />
            <span>Edit</span>
          </button>
          <button className="swipe-action-btn swipe-delete" onClick={() => { setOpenSwipeId(null); onDelete(player); }}>
            <FiTrash2 size={20} />
            <span>Delete</span>
          </button>
        </div>
      )}
      <div
        className="player-premium-card"
        ref={cardRef}
        onClick={handleCardClick}
        onTouchStart={hasActions ? onTouchStart : undefined}
        onTouchMove={hasActions ? onTouchMove : undefined}
        onTouchEnd={hasActions ? onTouchEnd : undefined}
      >
        <div className="player-card-avatar">
          {player.name.charAt(0).toUpperCase()}
        </div>
        <div className="player-card-info">
          <span className="player-card-name">{player.name}</span>
          <span className="player-card-hcp">HCP {player.handicap?.toFixed(1)}</span>
        </div>
      </div>
    </div>
  );
});

const PlayerManager = () => {
  const [players, setPlayers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openSwipeId, setOpenSwipeId] = useState(null);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);

  const navigate = useNavigate();
  const { authenticatedFetch } = useApi();
  const { currentSociety, hasPermission } = useAuth();

  const menuItems = [
    { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} /> },
    { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} /> },
    { name: 'Play Golf', route: '/play', icon: <GiGolfTee size={24} /> },
    { name: 'Manage', route: '/setup', icon: <FiSettings size={24} />, active: true },
  ];

  const fetchPlayers = useCallback(async () => {
    if (!currentSociety) return;
    try {
      const response = await authenticatedFetch('/players');
      const data = await response.json();
      setPlayers(data);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  }, [authenticatedFetch, currentSociety]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const handleOpenModal = (player = null) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedPlayer(null);
    setIsModalOpen(false);
  };

  const handleSavePlayer = async (playerData) => {
    const { id, name, handicap } = playerData;
    if (!name || handicap === '' || isNaN(handicap)) {
      notify.warn('Player name and a valid handicap are required.');
      return;
    }

    const method = id ? 'PUT' : 'POST';
    const url = id ? `/players/${id}` : `/players`;

    try {
      await authenticatedFetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, handicap: parseFloat(handicap) }),
      });

      fetchPlayers();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving player:", error);
      notify.error("Failed to save player. Please try again.");
    }
  };

  const handleDeleteClick = (player) => {
    setPlayerToDelete(player);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!playerToDelete) return;

    try {
      const response = await authenticatedFetch(`/players/${playerToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.status === 409) {
        const data = await response.json();
        notify.error(data.error);
      } else {
        fetchPlayers();
      }
    } catch (error) {
      console.error("Error deleting player:", error);
      notify.error("Failed to delete player.");
    }
    setIsConfirmOpen(false);
    setPlayerToDelete(null);
  };

  if (!currentSociety) {
    return (
      <div className="player-manager-page">
        <PageHeader title="Players" backTo="/society" backLabel="Back to The Society" />
        <div className="no-society-view" style={{ padding: '40px', textAlign: 'center' }}>
          <h3>No Society Selected</h3>
          <p>Please select or create a society to manage players.</p>
          <button onClick={() => navigate('/setup')} className="btn-primary-filled" style={{ marginTop: '20px' }}>Go to Setup</button>
        </div>
        <BottomNav menuItems={menuItems} />
      </div>
    );
  }

  const canManage = hasPermission(Permission.MANAGE_PLAYERS);

  const filteredPlayers = players
    .filter(player =>
      player.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const lastNameA = a.name.trim().split(' ').pop().toLowerCase();
      const lastNameB = b.name.trim().split(' ').pop().toLowerCase();
      if (lastNameA === lastNameB) {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }
      return lastNameA.localeCompare(lastNameB);
    });

  return (
    <div className="player-manager-page">
      <PlayerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSavePlayer}
        player={selectedPlayer}
      />

      <ConfirmationModal
        tone="danger"
        isOpen={isConfirmOpen}
        title="Delete Player?"
        message={`Are you sure you want to delete ${playerToDelete?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => { setIsConfirmOpen(false); setPlayerToDelete(null); }}
      />

      <PageHeader title="Players" backTo="/society" backLabel="Back to The Society" />

      <div className="player-manager-content-wrapper">
        <div className="player-manager-controls">

        </div>

        <div className="player-count-summary">
          Total Players in {currentSociety?.name} - {players.length}
        </div>

        <div className="player-search-container">
          <div className="search-box-modern">
            <svg className="search-icon-modern" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"/>
            </svg>
            <input
              type="text"
              className="search-input-modern"
              placeholder="Search by player name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {canManage && (
          <>
            <button className="btn-new" onClick={() => handleOpenModal()}>
              <FiPlus size={18} /> New Player
            </button>
            <p className="info-note">
              <FiInfo size={15} />
              <span>
                Players don't need an account. Add anyone here and they can be
                entered in competitions right away — if they join the society
                later they'll be linked to this profile automatically.
              </span>
            </p>
          </>
        )}

        <div className="player-cards-list">
          {filteredPlayers.map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
              canManage={canManage}
              onEdit={handleOpenModal}
              onDelete={handleDeleteClick}
              openSwipeId={openSwipeId}
              setOpenSwipeId={setOpenSwipeId}
            />
          ))}
        </div>
      </div>

      <BottomNav menuItems={menuItems} />
    </div>
  );
}

export default PlayerManager;
